import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box } from "@mui/material";
import { useAppSelector, useAppDispatch } from "../../hooks/sharedHooks";
import { ChatMessage } from "../models/chatBotModels";
import { Machine } from "../../../customer/machinery/models/models";
import { setMachineContext, closeMachinePicker } from "../chatBotSlice";
import ChatBotMessage from "./ChatBotMessage";
import ChatUserMessage from "./ChatUserMessage";
import ChatTypingIndicator from "./ChatTypingIndicator";
import ChatStreamingMessage from "./ChatStreamingMessage";
import ChatDaySeparator from "./ChatDaySeparator";
import ChatWelcome from "./ChatWelcome";
import ChatMachinePicker from "./ChatMachinePicker";
import useTranslation from "../../hooks/useTranslation";
import { dt } from "../../themes/redesignTokens";

// ═══════════════════════════════════════════════════════════════
//  MIDAS Design Tokens (local subset)
// ═══════════════════════════════════════════════════════════════


const midas = {
  gold: "#D4A017",
  goldDark: "#A67C00",
};

// ═══════════════════════════════════════════════════════════════
//  Helpers
// ═══════════════════════════════════════════════════════════════

/** Check if two timestamps are on different days */
const isDifferentDay = (a: string, b: string): boolean => {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() !== db.getFullYear() ||
    da.getMonth() !== db.getMonth() ||
    da.getDate() !== db.getDate()
  );
};

/** Check if two messages are more than 5 minutes apart */
const isNewGroup = (a: ChatMessage, b: ChatMessage): boolean => {
  const diff = Math.abs(
    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
  return diff > 5 * 60 * 1000;
};

// ═══════════════════════════════════════════════════════════════
//  ChatMessageList
// ═══════════════════════════════════════════════════════════════

interface ChatMessageListProps {
  onSuggestionClick: (text: string) => void;
  isPhone?: boolean;
  onImageClick?: (imageUrl: string, imageName: string) => void;
  /** MIDAS-SCOPESTREAM: Thread ID for buffer-scoped streaming reads.
   *  MIDAS passes activeThreadId; popup passes POPUP_THREAD_ID ("popup"). */
  threadId?: string;
}

const ChatMessageList: React.FC<ChatMessageListProps> = ({
  onSuggestionClick,
  isPhone = false,
  onImageClick,
  threadId,
}) => {
  const { translate } = useTranslation("chatBot");
  const dispatch = useAppDispatch();
  const messages = useAppSelector((state) => state.chatBot.messages);
  // MIDAS-SCOPESTREAM: Read streaming state from thread buffer (scoped by threadId).
  // When threadId is provided, buffer values take precedence over global state.
  // All hooks are called unconditionally to satisfy React hook rules.
  const threadBuffer = useAppSelector((state) =>
    threadId ? state.chatBot.threadBuffers[threadId] : undefined
  );
  const globalIsLoading = useAppSelector((state) => state.chatBot.isLoading);
  const globalCurrentStep = useAppSelector((state) => state.chatBot.currentStep);
  const globalStreamingText = useAppSelector((state) => state.chatBot.streamingText);
  const globalError = useAppSelector((state) => state.chatBot.error);
  const isLoading = threadId ? (threadBuffer?.isLoading || false) : globalIsLoading;
  const currentStep = threadId ? (threadBuffer?.currentStep || null) : globalCurrentStep;
  const streamingText = threadId ? (threadBuffer?.streamingText || null) : globalStreamingText;
  const error = threadId ? (threadBuffer?.error || null) : globalError;
  const showMachinePicker = useAppSelector((state) => state.chatBot.showMachinePicker);
  const machineContextLoading = useAppSelector((state) => state.chatBot.machineContextLoading);

  const handleMachineSelect = useCallback(
    (machine: Machine) => {
      dispatch(
        setMachineContext({
          machineId: machine.id,
          displayData: {
            model: machine.model,
            serialNumber: machine.serialNumber,
            internalNumber: machine.internalNumber,
            system: machine.system,
            manufacturerName: machine.manufacturer?.name,
            typeName: machine.type?.name,
            axisConfigName: machine.axisConfiguration?.name,
            controlSystemBrandName: machine.controlSystemBrand?.name,
            imageContent: machine.image?.content,
            description: machine.description,
            productionYear: machine.productionYear,
          },
        })
      );
    },
    [dispatch]
  );

  const handleClosePicker = useCallback(() => {
    dispatch(closeMachinePicker());
  }, [dispatch]);

  const listRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const [showNewMessagePill, setShowNewMessagePill] = useState(false);
  const prevMessageCount = useRef(messages.length);

  // Track scroll position to detect manual scroll-up
  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    const el = listRef.current;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setUserScrolledUp(distFromBottom > 100);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      if (!userScrolledUp) {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      } else {
        setShowNewMessagePill(true);
      }
    }
    prevMessageCount.current = messages.length;
  }, [messages.length, userScrolledUp]);

  // Auto-scroll when loading starts (typing indicator shown) or streaming text updates
  useEffect(() => {
    if (isLoading && !userScrolledUp) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isLoading, userScrolledUp, streamingText]);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    setShowNewMessagePill(false);
    setUserScrolledUp(false);
  };

  // Show inline machine picker when requested
  if (showMachinePicker) {
    return (
      <ChatMachinePicker
        onClose={handleClosePicker}
        onSelect={handleMachineSelect}
        loading={machineContextLoading}
      />
    );
  }

  // Show welcome state if no messages
  if (messages.length === 0 && !isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          background: `${dt.pageBg}`,
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(52,152,219,0.04) 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      >
        <ChatWelcome onSuggestionClick={onSuggestionClick} />
      </Box>
    );
  }

  return (
    <Box
      ref={listRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        padding: "16px",
        background: dt.pageBg,
        backgroundImage:
          "radial-gradient(circle at 1px 1px, rgba(52,152,219,0.04) 1px, transparent 0)",
        backgroundSize: "28px 28px",
        scrollBehavior: "smooth",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        position: "relative",
      }}
    >
      {messages.map((msg, index) => {
        const prev = index > 0 ? messages[index - 1] : null;
        const next = index < messages.length - 1 ? messages[index + 1] : null;

        // Day separator
        const showDaySeparator =
          !prev || isDifferentDay(prev.timestamp, msg.timestamp);

        // Group logic: same sender, within 5 minutes
        const isFirstInGroup =
          !prev || prev.role !== msg.role || isNewGroup(prev, msg);
        const isLastInGroup =
          !next || next.role !== msg.role || isNewGroup(msg, next);

        return (
          <React.Fragment key={msg.id}>
            {showDaySeparator && <ChatDaySeparator date={msg.timestamp} />}

            {msg.role === "assistant" ? (
              <ChatBotMessage
                message={msg}
                showAvatar={isFirstInGroup}
                showTimestamp={isLastInGroup}
                onSuggestionClick={onSuggestionClick}
                isPhone={isPhone}
              />
            ) : (
              <ChatUserMessage
                message={msg}
                showTimestamp={isLastInGroup}
                isPhone={isPhone}
                onImageClick={onImageClick}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* Streaming message — show progressive text when tokens are arriving */}
      {isLoading && streamingText && (
        <ChatStreamingMessage
          text={streamingText}
          showAvatar={
            messages.length === 0 ||
            messages[messages.length - 1]?.role !== "assistant"
          }
        />
      )}

      {/* Typing indicator — show only when loading but no tokens yet */}
      {isLoading && !streamingText && (
        <ChatTypingIndicator
          currentStep={currentStep}
          showAvatar={
            messages.length === 0 ||
            messages[messages.length - 1]?.role !== "assistant"
          }
        />
      )}

      {/* Error message */}
      {error && !isLoading && (
        <Box
          sx={{
            ml: "36px",
            fontFamily: dt.font,
            fontSize: "13px",
            color: "#E74C3C",
            backgroundColor: "rgba(231, 76, 60, 0.06)",
            border: "1px solid rgba(231, 76, 60, 0.15)",
            borderRadius: "8px",
            padding: "10px 14px",
          }}
        >
          {error || translate("unavailable")}
        </Box>
      )}

      {/* Scroll anchor */}
      <div ref={bottomRef} />

      {/* New message pill */}
      {showNewMessagePill && userScrolledUp && (
        <Box
          onClick={scrollToBottom}
          sx={{
            position: "sticky",
            bottom: "8px",
            alignSelf: "center",
            backgroundColor: midas.gold,
            color: "#FFFFFF",
            fontFamily: dt.font,
            fontSize: "12px",
            fontWeight: 600,
            padding: "6px 16px",
            borderRadius: "16px",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            zIndex: 10,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: midas.goldDark,
            },
          }}
        >
          {translate("newMessage")}
        </Box>
      )}
    </Box>
  );
};

export default ChatMessageList;
